import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetTagsQuery, useCreateTagMutation, useUpdateTagMutation, useDeleteTagMutation, useGetTagArticlesQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Pagination from '../../../components/Pagination';
import { confirmDelete } from '../../../utils/confirm';
import { showSuccess, showError } from '../../../utils/toast';

// Format date as "d M, Y h:i A" (e.g., "12 Aug, 2024 03:57 PM")
const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate().toString().padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = (hours % 12 || 12).toString().padStart(2, '0');
  return `${day} ${month}, ${year} ${displayHours}:${minutes} ${ampm}`;
};

const AdminArticleTags: React.FC = () => {
  const navigate = useNavigate();
  const [perPage, setPerPage] = useState(20);
  const [lang, setLang] = useState<'en' | 'ar'>('en');
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingTag, setEditingTag] = useState<any>(null);
  const [viewingTagId, setViewingTagId] = useState<number | null>(null);
  
  const { data, isLoading, refetch } = useGetTagsQuery({ per_page: perPage, lang, page });
  const [createTag, { isLoading: isCreating }] = useCreateTagMutation();
  const [updateTag, { isLoading: isUpdating }] = useUpdateTagMutation();
  const [deleteTag] = useDeleteTagMutation();
  const { data: tagArticlesData, isLoading: isLoadingArticles } = useGetTagArticlesQuery(viewingTagId || 0, { skip: !viewingTagId });

  const [formData, setFormData] = useState({
    tag: '',
    lang: 'en' as 'en' | 'ar',
  });

  const handlePerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPerPage(parseInt(e.target.value));
    setPage(1);
  };

  const handleLangChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLang(e.target.value as 'en' | 'ar');
    setPage(1);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddTag = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await createTag(formData).unwrap();
      showSuccess(result.message || 'Tag created successfully!');
      setShowAddModal(false);
      setFormData({ tag: '', lang: 'en' });
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to create tag. Please try again.');
    }
  };

  const handleEditTag = (tag: any) => {
    setEditingTag(tag);
    setFormData({ tag: tag.tag, lang: tag.lang });
    setShowEditModal(true);
  };

  const handleUpdateTag = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTag) return;
    
    try {
      const result = await updateTag({ id: editingTag.id, data: { tag: formData.tag } }).unwrap();
      showSuccess(result.message || 'Tag updated successfully!');
      setShowEditModal(false);
      setEditingTag(null);
      setFormData({ tag: '', lang: 'en' });
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to update tag. Please try again.');
    }
  };

  const handleDeleteTag = async (id: number) => {
    const confirmed = await confirmDelete('Are you sure?', "You won't be able to revert this!");
    if (!confirmed) return;

    try {
      const result = await deleteTag(id).unwrap();
      showSuccess(result.message || 'Tag deleted successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to delete tag. Please try again.');
    }
  };

  const handleViewArticles = (tagId: number) => {
    setViewingTagId(tagId);
    setShowViewModal(true);
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowViewModal(false);
    setEditingTag(null);
    setViewingTagId(null);
    setFormData({ tag: '', lang: 'en' });
  };

  if (isLoading) {
    return <PageLoader />;
  }

  const tags = data?.data || { data: [], current_page: 1, last_page: 1, total: 0, per_page: 20 };
  const tagArticles = tagArticlesData?.data || [];

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Article Tags</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Article Tags</li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <button type="button" className="btn btn-base btn-sm" onClick={() => setShowAddModal(true)}>
                New Tag
              </button>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-5">
                <form className="d-flex mb-3">
                  <label htmlFor="per_page" className="mt-3">Per Page: &nbsp;</label>
                  <select
                    id="per_page"
                    name="per_page"
                    className="form-control-solid form-select w-80px p-2"
                    value={perPage}
                    onChange={handlePerPageChange}
                  >
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="150">150</option>
                    <option value="300">300</option>
                    <option value="500">500</option>
                  </select>
                  <label htmlFor="lang" className="mt-3 mx-3">Language:</label>
                  <select
                    id="lang"
                    name="lang"
                    className="form-control-solid form-select w-100px p-2"
                    value={lang}
                    onChange={handleLangChange}
                  >
                    <option value="en">English</option>
                    <option value="ar">Arabic</option>
                  </select>
                </form>
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th>#</th>
                        <th>Name</th>
                        <th>Total Used</th>
                        <th>Created Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600 fs-6">
                      {tags.data.map((tag: any, index: number) => (
                        <tr key={tag.id}>
                          <td>{(tags.current_page - 1) * tags.per_page + index + 1}</td>
                          <td>{tag.tag}</td>
                          <td className="text-center">
                            <span className="badge badge-success">{tag.articles_count || 0}</span>
                            <br />
                            <button
                              className="btn btn-light btn-sm mt-1"
                              onClick={() => handleViewArticles(tag.id)}
                            >
                              Linked Articles
                            </button>
                          </td>
                          <td>{formatDateTime(tag.created_at)}</td>
                          <td className="text-center d-flex">
                            <button
                              type="button"
                              className="btn btn-sm h-30px btn-light-primary me-1"
                              onClick={() => handleEditTag(tag)}
                            >
                              <i className="fas fa-pencil"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-light-primary"
                              onClick={() => handleDeleteTag(tag.id)}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {tags.data.length > 0 && (
                  <div className="card-footer border-0 pt-6">
                    <Pagination
                      currentPage={tags.current_page}
                      lastPage={tags.last_page}
                      total={tags.total || 0}
                      perPage={tags.per_page || 20}
                      onPageChange={(newPage) => setPage(newPage)}
                      showTotal={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Tag</h5>
                <button type="button" className="btn-close" onClick={closeModals}></button>
              </div>
              <form onSubmit={handleAddTag}>
                <div className="modal-body">
                  <label htmlFor="tag" className="form-label">Tag Name</label>
                  <input
                    type="text"
                    id="tag"
                    name="tag"
                    className="form-control form-control-solid my-3"
                    placeholder="Enter Tag Name"
                    value={formData.tag}
                    onChange={handleInputChange}
                    required
                  />
                  <label htmlFor="lang" className="form-label">Language</label>
                  <select
                    id="lang"
                    name="lang"
                    className="form-control form-control-solid my-3"
                    value={formData.lang}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="en">English</option>
                    <option value="ar">Arabic</option>
                  </select>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModals}>
                    Close
                  </button>
                  <button type="submit" className="btn btn-base" disabled={isCreating}>
                    {isCreating ? 'Saving...' : 'Save changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingTag && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Tag</h5>
                <button type="button" className="btn-close" onClick={closeModals}></button>
              </div>
              <form onSubmit={handleUpdateTag}>
                <div className="modal-body">
                  <label htmlFor="edit_tag" className="form-label">Tag Name</label>
                  <input
                    type="text"
                    id="edit_tag"
                    name="tag"
                    className="form-control form-control-solid my-3"
                    placeholder="Enter Tag Name"
                    value={formData.tag}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModals}>
                    Close
                  </button>
                  <button type="submit" className="btn btn-base" disabled={isUpdating}>
                    {isUpdating ? 'Saving...' : 'Save changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Articles Modal */}
      {showViewModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Linked Articles</h5>
                <button type="button" className="btn-close" onClick={closeModals}></button>
              </div>
              <div className="modal-body">
                {isLoadingArticles ? (
                  <div className="text-center py-5">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : tagArticles.length > 0 ? (
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Title</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tagArticles.map((article: any) => (
                        <tr key={article.id}>
                          <td>{article.id}</td>
                          <td>{article.title}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-center py-5 text-muted">No articles linked to this tag.</p>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModals}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminArticleTags;

